import { useFetchBatch } from './useFetchBatch'
import { useQueryClientUtils } from '../useQueryClientUtils'
import { queryKeys as queryKeysParty } from '../party/useQueryParty'
import { queryKeys as queryKeysFolder } from '../folder/useQueryFolder'
import { queryKeys as queryKeysSearch } from '../search/useQuerySearch'
import { queryKeys as queryKeysDocument } from '../document/useQueryDocument'
import { responseDataTypeName as responseDataTypeFolder } from '../folder/useFetchFolder'
import { queryKeys as queryKeysDocumentTags } from '../document-tags/useQueryDocumentTags'

const getQueryKeysForTagMutations = (workspaceId: string) => [
  [queryKeysDocument._def, workspaceId],
  [queryKeysDocument.byId._def, workspaceId],
  queryKeysDocumentTags.all({ workspaceId }).queryKey,
  queryKeysDocument.allInfinite({ workspaceId }).queryKey,
  queryKeysDocument.all({ workspaceId }).queryKey,
]

const getQueryKeysForMoveToFolderMutation = (workspaceId: string) => [
  [queryKeysDocument._def, workspaceId],
  [queryKeysDocument.byId._def, workspaceId],
  [queryKeysFolder._def, workspaceId],
  [queryKeysFolder.byId._def],
  [queryKeysSearch.all({ workspaceId, type: responseDataTypeFolder })],
  queryKeysDocument.allInfinite({ workspaceId }).queryKey,
  queryKeysDocument.all({ workspaceId }).queryKey,
  queryKeysFolder.all({ workspaceId }).queryKey,
  queryKeysFolder.allUnpaginated({ workspaceId }).queryKey,
  queryKeysFolder.sharedWithMeUnpaginated({ workspaceId }).queryKey,
]

const getQueryKeysForTrashMutation = (workspaceId: string) => [
  ...getQueryKeysForMoveToFolderMutation(workspaceId),
  [queryKeysParty.all({ workspaceId })],
  queryKeysDocument.allInfinite({ workspaceId }).queryKey,
  queryKeysDocument.all({ workspaceId }).queryKey,
  queryKeysFolder.all({ workspaceId }).queryKey,
  queryKeysFolder.allUnpaginated({ workspaceId }).queryKey,
  queryKeysFolder.sharedWithMeUnpaginated({ workspaceId }).queryKey,
]

const getQueryKeysForMoveToWorkspaceMutation = (workspaceId: string) => [
  [queryKeysDocument._def, workspaceId],
  [queryKeysDocument.byId._def],
  [queryKeysFolder._def, workspaceId],
  [queryKeysFolder.byId._def],
  [queryKeysSearch.all({ workspaceId, type: responseDataTypeFolder })],
  queryKeysDocument.allInfinite({ workspaceId }).queryKey,
  queryKeysDocument.all({ workspaceId }).queryKey,
  queryKeysFolder.all({ workspaceId }).queryKey,
  queryKeysFolder.allUnpaginated({ workspaceId }).queryKey,
  queryKeysFolder.sharedWithMeUnpaginated({ workspaceId }).queryKey,
]

export const useMutationBatchOnSettled = () => {
  const { invalidateQueries } = useQueryClientUtils()

  const onSettledAddTags = (
    variables: { workspaceId: string },
    responseType?: App.Internals.Enums.BatchApiResponseType,
  ) => {
    if (responseType === 'queued')
      return

    invalidateQueries(getQueryKeysForTagMutations(variables.workspaceId))
  }

  const onSettledRemoveTags = (
    variables: { workspaceId: string },
    responseType?: App.Internals.Enums.BatchApiResponseType,
  ) => {
    if (responseType === 'queued')
      return

    invalidateQueries(getQueryKeysForTagMutations(variables.workspaceId))
  }

  const onSettledMoveToFolder = (
    variables: { workspaceId: string },
    responseType?: App.Internals.Enums.BatchApiResponseType,
  ) => {
    if (responseType === 'queued')
      return

    invalidateQueries(getQueryKeysForMoveToFolderMutation(variables.workspaceId))
  }

  const onSettledTrash = (
    variables: { workspaceId: string },
    responseType?: App.Internals.Enums.BatchApiResponseType,
  ) => {
    if (responseType === 'queued')
      return

    invalidateQueries(getQueryKeysForTrashMutation(variables.workspaceId))
  }

  return {
    onSettledAddTags,
    onSettledRemoveTags,
    onSettledMoveToFolder,
    onSettledTrash,
  }
}

const addTags = () => {
  const { addTags: mutationFn } = useFetchBatch()
  const { cancelQueries } = useQueryClientUtils()
  const { onSettledAddTags } = useMutationBatchOnSettled()

  return useMutation({
    mutationFn,
    onMutate: ({ workspaceId }) => {
      cancelQueries(getQueryKeysForTagMutations(workspaceId))
    },
    onSettled: (response, _, variables) => {
      onSettledAddTags(variables, response?.data.status)
    },
  })
}

const removeTags = () => {
  const { removeTags: mutationFn } = useFetchBatch()
  const { cancelQueries } = useQueryClientUtils()
  const { onSettledRemoveTags } = useMutationBatchOnSettled()

  return useMutation({
    mutationFn,
    onMutate: ({ workspaceId }) => {
      cancelQueries(getQueryKeysForTagMutations(workspaceId))
    },
    onSettled: (response, _, variables) => {
      onSettledRemoveTags(variables, response?.data.status)
    },
  })
}

const moveToFolder = () => {
  const { moveToFolder: mutationFn } = useFetchBatch()
  const { cancelQueries } = useQueryClientUtils()
  const { onSettledMoveToFolder } = useMutationBatchOnSettled()

  return useMutation({
    mutationFn,
    onMutate: ({ workspaceId }) => {
      cancelQueries(getQueryKeysForMoveToFolderMutation(workspaceId))
    },
    onSettled: (response, _, variables) => {
      onSettledMoveToFolder(variables, response?.data.status)
    },
  })
}

const trash = () => {
  const { trash: mutationFn } = useFetchBatch()
  const { cancelQueries } = useQueryClientUtils()
  const { onSettledTrash } = useMutationBatchOnSettled()

  return useMutation({
    mutationFn,
    onMutate: ({ workspaceId }) => {
      cancelQueries(getQueryKeysForTrashMutation(workspaceId))
    },
    onSettled: (response, _, variables) => {
      onSettledTrash(variables, response?.data.status)
    },
  })
}

const copyToWorkspace = () => {
  const { copyToWorkspace: mutationFn } = useFetchBatch()
  const { cancelQueries } = useQueryClientUtils()

  return useMutation({
    mutationFn,
    onMutate: ({ workspaceId }) => {
      cancelQueries(getQueryKeysForMoveToWorkspaceMutation(workspaceId))
    },
  })
}

const requestDocumentDataExport = () => {
  const { requestDocumentDataExport } = useFetchBatch()

  return useMutation({
    mutationFn: requestDocumentDataExport,
  })
}

export const useMutationBatch = {
  addTags,
  removeTags,
  moveToFolder,
  trash,
  copyToWorkspace,
  requestDocumentDataExport,
}
